import { mediaList } from "@/mocks/media";
import type { MediaItem } from "@/types/media";

export type SortKey = "updated" | "views" | "rating";

export interface FilterOptions {
  region?: string;
  year?: string;
  minRating?: number;
  genre?: string;
  sort?: SortKey;
}

export function getMediaById(id: string): MediaItem | undefined {
  return mediaList.find((item) => item.id === id);
}

/** Resolve the category slug into a base media subset. */
export function getCategoryMedia(type: string): MediaItem[] {
  switch (type) {
    case "movie":
      return mediaList.filter((m) => m.type === "电影");
    case "tv":
      return mediaList.filter((m) => m.type === "电视剧");
    case "china":
      return mediaList.filter((m) => m.region === "国产");
    case "overseas":
      return mediaList.filter((m) => m.region === "海外");
    case "popular":
      return [...mediaList].sort((a, b) => b.views - a.views).slice(0, 12);
    default:
      return [...mediaList];
  }
}

export function applyFilters(items: MediaItem[], options: FilterOptions): MediaItem[] {
  let result = [...items];

  if (options.region && options.region !== "全部") {
    result = result.filter((m) => m.region === options.region);
  }
  if (options.year && options.year !== "全部") {
    result = result.filter((m) => String(m.year) === options.year);
  }
  if (options.minRating) {
    result = result.filter((m) => m.rating >= options.minRating!);
  }
  if (options.genre && options.genre !== "全部") {
    result = result.filter((m) => m.genres.includes(options.genre!));
  }

  const sort = options.sort ?? "updated";
  if (sort === "views") {
    result.sort((a, b) => b.views - a.views);
  } else if (sort === "rating") {
    result.sort((a, b) => b.rating - a.rating);
  } else {
    result.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1));
  }

  return result;
}

export function searchMedia(keyword: string): MediaItem[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return mediaList.filter((m) => {
    const haystack = [m.title, m.enTitle, m.synopsis, m.director, m.genres.join(" "), m.actors.join(" ")]
      .join(" ")
      .toLowerCase();
    return haystack.includes(kw);
  });
}

/** Front-end related recommendations based on tag similarity. */
export function getSimilarMedia(item: MediaItem, limit = 4): MediaItem[] {
  return mediaList
    .filter((m) => m.id !== item.id)
    .map((m) => {
      const overlap = m.genres.filter((g) => item.genres.includes(g)).length;
      const sameRegion = m.region === item.region ? 0.5 : 0;
      return { item: m, score: overlap + sameRegion };
    })
    .sort((a, b) => b.score - a.score || b.item.rating - a.item.rating)
    .slice(0, limit)
    .map((entry) => entry.item);
}

/** Unique metadata values for filter controls. */
export function getFilterMeta(items: MediaItem[]) {
  const years = Array.from(new Set(items.map((m) => String(m.year)))).sort((a, b) => Number(b) - Number(a));
  const genres = Array.from(new Set(items.flatMap((m) => m.genres)));
  return { years, genres };
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)} 万`;
  return String(views);
}